import { gettext } from '@eventbrite/i18n';
import {
    CalendarIcon,
    ChevronDownIcon,
    HelpIcon,
    TicketIcon,
} from '@eventbrite/marmalade';
import React from 'react';
import { navigationProps } from '../modules/types';
import { alternativePath } from '../utils';

export const defaultNavigation = ({
    helpCenterLanguage,
    tld,
    intentToggle,
}: {
    helpCenterLanguage: string;
    tld: string;
    intentToggle?: { show?: boolean };
}): navigationProps => {
    const navItems = {
        desktop: [
            {
                path: 'product-updates',
                translatedText: gettext('Updates'),
                heapIdentifier: 'Updates',
            },
            {
                path: alternativePath('l/contact-eventbrite-sales/', tld),
                translatedText: gettext('Contact Sales'),
                heapIdentifier: 'Contact Sales',
            },
            {
                path: 'organizer/overview/',
                translatedText: gettext('Create Events'),
                heapIdentifier: 'Create Events',
            },
            {
                translatedText: gettext('Help Center'),
                heapIdentifier: 'Help Center',
                iconType: <ChevronDownIcon />,
                iconPosition: 'right',
                dropdownItems: [
                    {
                        path: `help/${helpCenterLanguage}/`,
                        translatedText: gettext('Help Center'),
                        heapIdentifier: 'Help Center',
                    },
                    {
                        path: `help/${helpCenterLanguage}/articles/319355/where-are-my-tickets/`,
                        translatedText: gettext('Find your tickets'),
                        heapIdentifier: 'Find your tickets',
                    },
                    {
                        path: `help/${helpCenterLanguage}/articles/647151/how-to-contact-the-event-organizer/`,
                        translatedText: gettext('Contact your event organizer'),
                        heapIdentifier: 'Contact your event organizer',
                    },
                ],
            },
        ],

        mobile: [
            {
                path: 'd/local/events/',
                translatedText: gettext('Find Events'),
                heapIdentifier: 'Find Events',
                iconType: <TicketIcon />,
            },
            {
                translatedText: gettext('Create Events'),
                heapIdentifier: 'Create Events',
                iconType: <CalendarIcon />,
                dropdownItems: [
                    {
                        translatedText: gettext('Solutions'),
                        heapIdentifier: 'Solutions',
                        dropdownItems: [
                            {
                                translatedText: gettext('Event Ticketing'),
                                heapIdentifier: 'Event Ticketing',
                                path: alternativePath('l/sell-tickets/', tld),
                            },
                            {
                                path: 'organizer/features/event-marketing-platform/',
                                translatedText: gettext(
                                    'Event Marketing Platform',
                                ),
                                heapIdentifier: 'Marketing Suite',
                            },
                            {
                                path: alternativePath('l/eventbrite-ads/', tld),
                                translatedText: gettext('Eventbrite Ads'),
                                heapIdentifier: 'Eventbrite Ads',
                            },
                            {
                                path: alternativePath('l/event-payment/', tld),
                                translatedText: gettext('Payments'),
                                heapIdentifier: 'Payments',
                            },
                        ],
                    },
                    {
                        translatedText: gettext('Industry'),
                        heapIdentifier: 'Industry',
                        dropdownItems: [
                            {
                                path: alternativePath('l/music/', tld),
                                translatedText: gettext('Music'),
                                heapIdentifier: 'Music',
                            },
                            {
                                path: 'organizer/event-industry/food-drink-event-ticketing/',
                                translatedText: gettext('Food & Beverage'),
                                heapIdentifier: 'Food & Beverage',
                            },
                            {
                                path: 'organizer/event-industry/performing-arts/',
                                translatedText: gettext('Performing Arts'),
                                heapIdentifier: 'Performing Arts',
                            },
                            {
                                path: alternativePath('l/npo/', tld),
                                translatedText: gettext('Charity & Causes'),
                                heapIdentifier: 'Charity & Causes',
                            },
                            {
                                path: 'organizer/event-format/host-retail-events/',
                                translatedText: gettext('Retail'),
                                heapIdentifier: 'Retail',
                            },
                        ],
                    },
                    {
                        translatedText: gettext('Event Types'),
                        heapIdentifier: 'Event Types',
                        dropdownItems: [
                            {
                                path: 'organizer/event-type/music-venues/',
                                translatedText: gettext('Concerts'),
                                heapIdentifier: 'Concerts',
                            },
                            {
                                path: 'organizer/event-type/create-a-workshop/',
                                translatedText: gettext('Classes & Workshops'),
                                heapIdentifier: 'Classes & Workshops',
                            },
                            {
                                path: 'organizer/event-type/festival-solutions/',
                                translatedText: gettext('Festivals & Fairs'),
                                heapIdentifier: 'Festivals & Fairs',
                            },
                            {
                                path: alternativePath('l/conferences/', tld),
                                translatedText: gettext('Conferences'),
                                heapIdentifier: 'Conferences',
                            },
                            {
                                path: 'organizer/event-type/eventbrite-for-business/',
                                translatedText: gettext('Corporate Events'),
                                heapIdentifier: 'Corporate Events',
                            },
                            {
                                path: 'organizer/event-type/virtual-events-platform/',
                                translatedText: gettext('Online Events'),
                                heapIdentifier: 'Online Events',
                            },
                        ],
                    },
                    {
                        translatedText: gettext('Blog'),
                        heapIdentifier: 'Blog',
                        dropdownItems: [
                            {
                                path: 'blog/category/tips-and-guides/',
                                translatedText: gettext('Tips & Guides'),
                                heapIdentifier: 'Tips & Guides',
                            },
                            {
                                path: 'blog/category/news-and-trends/',
                                translatedText: gettext('News & Trends'),
                                heapIdentifier: 'News & Trends',
                            },
                            {
                                path: 'blog/category/community/',
                                translatedText: gettext('Community'),
                                heapIdentifier: 'Community',
                            },
                            {
                                path: 'blog/category/tools-and-features/',
                                translatedText: gettext('Tools & Features'),
                                heapIdentifier: 'Tools & Features',
                            },
                        ],
                    },
                ],
            },
            {
                path: 'organizer/overview/',
                translatedText: gettext('Create Events'),
                heapIdentifier: 'Create Events',
            },
            {
                path: alternativePath('l/contact-eventbrite-sales/', tld),
                translatedText: gettext('Contact Sales'),
                heapIdentifier: 'Contact Sales',
            },
            {
                // TODO: Verify if this link always assumes creator intent
                path: 'signin/signup/?referrer=/manage/events/create/',
                translatedText: gettext('Get Started'),
                heapIdentifier: 'Get Started',
                textColor: '#3659E3',
            },
            {
                translatedText: gettext('Help Center'),
                heapIdentifier: 'Help Center',
                iconType: <HelpIcon />,
                dropdownItems: [
                    {
                        path: `help/${helpCenterLanguage}/`,
                        translatedText: gettext('Help Center'),
                        heapIdentifier: 'Help Center',
                    },
                    {
                        path: `help/${helpCenterLanguage}/articles/319355/where-are-my-tickets/`,
                        translatedText: gettext('Find your tickets'),
                        heapIdentifier: 'Find your tickets',
                    },
                    {
                        path: `help/${helpCenterLanguage}/articles/647151/how-to-contact-the-event-organizer/`,
                        translatedText: gettext('Contact your event organizer'),
                        heapIdentifier: 'Contact your event organizer',
                    },
                ],
            },
            {
                path: 'product-updates',
                translatedText: gettext('Updates'),
                heapIdentifier: 'Updates',
            },
        ],
    };
    if (tld === '.com') {
        navItems.mobile.splice(2, 0, {
            path: 'resources/',
            translatedText: gettext('Organizer Resource Hub'),
            heapIdentifier: 'Organizer Resource Hub',
        });
    }

    if (intentToggle?.show) {
        navItems.mobile = [
            {
                path: '/signin',
                translatedText: gettext('Log In'),
                heapIdentifier: 'login',
            },
            {
                path: '/signin/signup',
                translatedText: gettext('Sign Up'),
                heapIdentifier: 'signup',
            },
            {
                path: '/mytickets',
                translatedText: gettext('Find my tickets'),
                heapIdentifier: 'find-my-tickets',
            },
            ...navItems.mobile,
        ];
    }

    return navItems;
};
